import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminCreateTagCategoryMutation,
  InternalAdminCreateTagCategoryMutationVariables,
  InternalAdminEditTagCategoryMutation,
  InternalAdminEditTagCategoryMutationVariables,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { SERVER_ERROR } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { GreenCheckIcon } from "modules/icons";
import INTERNAL_ADMIN_CREATE_TAG_CATEGORY_MUTATION from "../_networking/internal-admin-create-tag-category";
import INTERNAL_ADMIN_EDIT_TAG_CATEGORY_MUTATION from "../_networking/internal-admin-edit-tag-category";
import useInternalAdminTagCategorys from "../hooks/use-internal-admin-tag-categories";
import useQueryString from "modules/hooks/use-query-string";
import { getMessageFromError } from "modules/utils/Functions";

interface IProps {
  tagCategoryId?: string;
}

const InternalAdminTagCategoryModal = ({ tagCategoryId }: IProps) => {
  const { closeModal, openModal } = useOverlay();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { qsOrganizationId } = useQueryString();

  const [internalAdminEditTagCategoryMutationAPI] = useMutation<
    InternalAdminEditTagCategoryMutation,
    InternalAdminEditTagCategoryMutationVariables
  >(INTERNAL_ADMIN_EDIT_TAG_CATEGORY_MUTATION);

  const [internalAdminCreateTagCategoryMutationAPI] = useMutation<
    InternalAdminCreateTagCategoryMutation,
    InternalAdminCreateTagCategoryMutationVariables
  >(INTERNAL_ADMIN_CREATE_TAG_CATEGORY_MUTATION);

  const { tagCategory } = useInternalAdminTagCategorys(tagCategoryId);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    reset,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      SERVER_ERROR: "",
      name: "",
    },
  });

  useEffect(() => {
    if (tagCategory) {
      reset({
        SERVER_ERROR: "",
        name: tagCategory.name,
      });
    }
  }, [tagCategory, reset]);

  const onSubmit = useCallback(
    async ({ name }: FieldValues) => {
      if (!qsOrganizationId) return;
      setIsSubmitting(true);
      if (tagCategory) {
        internalAdminEditTagCategoryMutationAPI({
          variables: {
            organizationId: qsOrganizationId,
            id: tagCategory.id,
            input: {
              name,
            },
          },
          onError: (error: Error) => {
            setIsSubmitting(false);
            setError(SERVER_ERROR, {
              message: error.message,
            });
          },
          onCompleted: () => {
            closeModal();
            openModal({
              type: MODAL_TYPE.HELP,
              helpModal: {
                icon: <GreenCheckIcon boxSize={100} />,
                title: "Category Edited",
                message: `Tag category, "${tagCategory.name}" has been edited!`,
              },
            });
          },
        });
      } else {
        internalAdminCreateTagCategoryMutationAPI({
          variables: {
            organizationId: qsOrganizationId,
            input: {
              name,
            },
          },
          onCompleted: (data) => {
            const tagCategory = data.internalAdminCreateTagCategory;
            if (!tagCategory) {
              return;
            }
            closeModal();
            openModal({
              type: MODAL_TYPE.HELP,
              helpModal: {
                icon: <GreenCheckIcon boxSize={100} />,
                title: "Category Added",
                message: `Tag category, "${tagCategory.name}" has been created!`,
              },
            });
          },
          onError: (error: Error) => {
            setIsSubmitting(false);
            setError(SERVER_ERROR, {
              message: error.message,
            });
          },
        });
      }
    },
    [
      closeModal,
      internalAdminCreateTagCategoryMutationAPI,
      internalAdminEditTagCategoryMutationAPI,
      openModal,
      qsOrganizationId,
      setError,
      tagCategory,
    ],
  );

  if (tagCategoryId && !tagCategory) {
    return null;
  }

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
              {tagCategory ? "Edit Tag Category" : "Add Tag Category"}
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <form id="admin-tag -form" onSubmit={handleSubmit(onSubmit)}>
            <VStack alignItems="flex-start" spacing={4}>
              <FormControl isInvalid={!!errors?.name}>
                <Input
                  id="name"
                  defaultValue={tagCategory ? tagCategory.name : undefined}
                  placeholder="Create unique category name"
                  {...register("name", {
                    required: {
                      message: "a name is required",
                      value: true,
                    },
                  })}
                />
                <FormErrorMessage>
                  {getMessageFromError(errors?.name?.message)}
                </FormErrorMessage>
              </FormControl>
            </VStack>
            {errors.SERVER_ERROR && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.SERVER_ERROR?.message)}
              </Text>
            )}
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminTagCategoryModal);
